import type { SegmentDefinition, SegmentCondition } from '@masters-club/validation';
import type { BroadcastFilters } from '../types';

interface WithGroups {
  groups: unknown;
}

function hasGroupsProperty(value: object): value is WithGroups {
  return 'groups' in value;
}

/**
 * Type guard: проверяет, является ли значение SegmentDefinition (новый формат).
 * SegmentDefinition содержит поле 'groups' (Array); legacy BroadcastFilters — нет.
 */
export function isSegmentDefinition(
  value: BroadcastFilters | SegmentDefinition | null | undefined,
): value is SegmentDefinition {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if (!hasGroupsProperty(value)) {
    return false;
  }
  return Array.isArray(value.groups);
}

/**
 * Type guard: проверяет, является ли значение legacy BroadcastFilters.
 */
export function isBroadcastFilters(
  value: BroadcastFilters | SegmentDefinition | null | undefined,
): value is BroadcastFilters {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return !hasGroupsProperty(value);
}

/**
 * Конвертирует legacy BroadcastFilters → SegmentDefinition.
 * Используется в TabRecipients для передачи данных в новый parent API.
 * Поля, которые нельзя точно выразить через реестр сегментов (productIds, courseIds,
 * channelIds) — опускаются: parent-хук отправит их как legacy-поле на backend.
 */
export function broadcastFiltersToSegmentDefinition(filters: BroadcastFilters): SegmentDefinition {
  const conditions: SegmentCondition[] = [];

  if (typeof filters.isActive === 'boolean') {
    conditions.push({ field: 'isActive', operator: 'eq', value: filters.isActive });
  }

  if (filters.hasTelegram === true) {
    conditions.push({ field: 'telegramId', operator: 'isSet' });
  }

  if (filters.hasEmail === true) {
    conditions.push({ field: 'email', operator: 'isSet' });
  }

  if (filters.registeredAfter) {
    conditions.push({ field: 'createdAt', operator: 'gte', value: filters.registeredAfter });
  }

  if (filters.registeredBefore) {
    conditions.push({ field: 'createdAt', operator: 'lte', value: filters.registeredBefore });
  }

  if (conditions.length === 0) {
    return { groupsOp: 'AND', groups: [] };
  }

  return {
    groupsOp: 'AND',
    groups: [{ conditionsOp: 'AND', conditions }],
  };
}
